import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth0 } from '@auth0/auth0-react';

interface WebSocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  connectionError: string | null;
  joinSession: (sessionId: string) => void;
  leaveSession: (sessionId: string) => void;
  onTranscriptUpdate: (callback: (data: Record<string, unknown>) => void) => void;
  onTranscriptDelete: (callback: (data: Record<string, unknown>) => void) => void;
  onSessionStatusChange: (callback: (data: Record<string, unknown>) => void) => void;
  onGenerationStatus: (callback: (data: Record<string, unknown>) => void) => void;
  onAIResponse: (callback: (data: Record<string, unknown>) => void) => void;
  offTranscriptUpdate: (callback: (data: Record<string, unknown>) => void) => void;
  offTranscriptDelete: (callback: (data: Record<string, unknown>) => void) => void;
  offSessionStatusChange: (callback: (data: Record<string, unknown>) => void) => void;
  offGenerationStatus: (callback: (data: Record<string, unknown>) => void) => void;
  offAIResponse: (callback: (data: Record<string, unknown>) => void) => void;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);

  // Get the WebSocket server URL from environment or use the backend server
  const getWebSocketUrl = () => {
    if (import.meta.env.VITE_WEBSOCKET_URL) {
      return import.meta.env.VITE_WEBSOCKET_URL;
    }

    // Use the existing API server URL from environment
    if (import.meta.env.VITE_API_SERVER_URL) {
      return import.meta.env.VITE_API_SERVER_URL;
    } else {
      throw new Error('No WebSocket URL found');
    }
  };

  const connect = useCallback(
    async (isRetry = false) => {
      if (!isAuthenticated) return;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      try {
        const token = await getAccessTokenSilently();

        const connectionOptions = {
          auth: {
            token
          },
          transports: ['websocket', 'polling'],
          timeout: 20000,
          reconnection: true,
          reconnectionAttempts: 10,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 10000,
          maxReconnectionAttempts: 10,
          pingTimeout: 60000,
          pingInterval: 25000,
          path: '/ws/',
          upgrade: true,
          connectionTimeout: 20000,
          forceNew: false,
          rememberUpgrade: true
        };

        const newSocket = io(getWebSocketUrl(), connectionOptions);

        newSocket.on('connect', () => {
          console.log('WebSocket connected successfully', {
            id: newSocket.id,
            transport: newSocket.io.engine.transport.name,
            url: getWebSocketUrl(),
            isRetry,
            attempt: reconnectAttemptsRef.current
          });
          setIsConnected(true);
          setConnectionError(null);
          setSocket(newSocket);
          socketRef.current = newSocket;
          reconnectAttemptsRef.current = 0;
        });

        newSocket.on('disconnect', (reason) => {
          console.warn('WebSocket disconnected', {
            reason,
            transport: newSocket.io.engine.transport.name,
            wasConnected: newSocket.connected
          });
          setIsConnected(false);
          setSocket(null);
          socketRef.current = null;
        });

        newSocket.on('connect_error', (error) => {
          console.error('WebSocket connection error:', error);
          setIsConnected(false);
          setSocket(null);
          socketRef.current = null;

          if (error.message?.includes('websocket') || error.message?.includes('transport')) {
            const errorMessage =
              'WebSocket connection failed. This may be due to an ad blocker like uBlock Origin. Please try disabling it or adding this site to your whitelist.';
            console.warn(errorMessage);
            setConnectionError(errorMessage);
          } else {
            setConnectionError(error.message || 'Connection failed');
          }

          if (isAuthenticated && reconnectAttemptsRef.current < 10) {
            reconnectAttemptsRef.current += 1;
            const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current - 1), 30000);

            console.log(
              `Scheduling reconnection attempt ${reconnectAttemptsRef.current} in ${delay}ms`
            );

            reconnectTimeoutRef.current = setTimeout(() => {
              connect(true);
            }, delay);
          } else if (reconnectAttemptsRef.current >= 10) {
            console.error('Max reconnection attempts reached');
            setConnectionError('Max reconnection attempts reached. Please refresh the page.');
          }
        });

        newSocket.on('reconnect', () => {
          setIsConnected(true);
          setConnectionError(null);
          setSocket(newSocket);
          socketRef.current = newSocket;
        });

        newSocket.on('reconnect_error', (error) => {
          console.error('WebSocket reconnection error:', error);
        });

        newSocket.on('reconnect_failed', () => {
          console.error('WebSocket reconnection failed');
        });

        newSocket.on('ping', () => {
          newSocket.emit('pong');
        });

        newSocket.io.engine.on('upgrade', () => {
          console.log('WebSocket transport upgraded', {
            from: 'polling',
            to: 'websocket',
            id: newSocket.id
          });
        });

        newSocket.io.engine.on('error', (error) => {
          console.error('WebSocket engine error', {
            error: error instanceof Error ? error.message : String(error),
            transport: newSocket.io.engine.transport.name,
            id: newSocket.id
          });
        });
      } catch (error) {
        console.error('Failed to get access token for WebSocket:', error);
      }
    },
    [isAuthenticated, getAccessTokenSilently]
  );

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    setSocket(null);
    setIsConnected(false);
    reconnectAttemptsRef.current = 0;
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      const timer = setTimeout(() => {
        connect();
      }, 100);

      return () => {
        clearTimeout(timer);
      };
    } else {
      disconnect();
    }
  }, [isAuthenticated, connect, disconnect]);

  useEffect(() => {
    return () => {
      if (socketRef.current) {
        disconnect();
      }
    };
  }, [disconnect]);

  const joinSession = useCallback(
    (sessionId: string) => {
      if (socketRef.current && isConnected) {
        socketRef.current.emit('join-session', sessionId);
        console.log(`Joined session room: ${sessionId}`);
      }
    },
    [isConnected]
  );

  const leaveSession = useCallback(
    (sessionId: string) => {
      if (socketRef.current && isConnected) {
        socketRef.current.emit('leave-session', sessionId);
        console.log(`Left session room: ${sessionId}`);
      }
    },
    [isConnected]
  );

  const onTranscriptUpdate = useCallback(
    (callback: (data: Record<string, unknown>) => void) => {
      if (socketRef.current) {
        socketRef.current.on('new-transcript', callback);
        socketRef.current.on('transcript-update', callback);
      }
    },
    [socket]
  );

  const offTranscriptUpdate = useCallback(
    (callback: (data: Record<string, unknown>) => void) => {
      if (socketRef.current) {
        socketRef.current.off('new-transcript', callback);
        socketRef.current.off('transcript-update', callback);
      }
    },
    [socket]
  );

  const onTranscriptDelete = useCallback(
    (callback: (data: Record<string, unknown>) => void) => {
      if (socketRef.current) {
        socketRef.current.on('transcript-delete', callback);
      }
    },
    [socket]
  );

  const offTranscriptDelete = useCallback(
    (callback: (data: Record<string, unknown>) => void) => {
      if (socketRef.current) {
        socketRef.current.off('transcript-delete', callback);
      }
    },
    [socket]
  );

  const onSessionStatusChange = useCallback(
    (callback: (data: Record<string, unknown>) => void) => {
      if (socketRef.current) {
        socketRef.current.on('session-status-change', callback);
      }
    },
    [socket]
  );

  const offSessionStatusChange = useCallback(
    (callback: (data: Record<string, unknown>) => void) => {
      if (socketRef.current) {
        socketRef.current.off('session-status-change', callback);
      }
    },
    [socket]
  );

  const onGenerationStatus = useCallback(
    (callback: (data: Record<string, unknown>) => void) => {
      if (socketRef.current) {
        socketRef.current.on('generation-status', callback);
      }
    },
    [socket]
  );

  const offGenerationStatus = useCallback(
    (callback: (data: Record<string, unknown>) => void) => {
      if (socketRef.current) {
        socketRef.current.off('generation-status', callback);
      }
    },
    [socket]
  );

  const onAIResponse = useCallback(
    (callback: (data: Record<string, unknown>) => void) => {
      if (socketRef.current && isConnected) {
        socketRef.current.on('ai-response', callback);
      }
    },
    [socket, isConnected]
  );

  const offAIResponse = useCallback(
    (callback: (data: Record<string, unknown>) => void) => {
      if (socketRef.current) {
        socketRef.current.off('ai-response', callback);
      }
    },
    [socket]
  );

  const value: WebSocketContextType = {
    socket,
    isConnected,
    connectionError,
    joinSession,
    leaveSession,
    onTranscriptUpdate,
    onTranscriptDelete,
    onSessionStatusChange,
    onGenerationStatus,
    onAIResponse,
    offTranscriptUpdate,
    offTranscriptDelete,
    offSessionStatusChange,
    offGenerationStatus,
    offAIResponse
  };

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};

export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
